import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Row,
  Space,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  generatePath,
  Link,
  useParams,
  useSearchParams,
} from 'react-router';

import { i18n, PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { Layout, TextLink } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { getBillingProductBlockReason, organizationModel } from '@/entities/organization';
import { organizationsModel } from '@/entities/organizations';
import { userModel } from '@/entities/user';

import { AccountStripeSwitch } from '@/features/accountStripeSwitch';

import { BillingHistory } from '@/widgets/billingHistory';
import { BillingInfo } from '@/widgets/billingInfo';
import { CurrentSubscription } from '@/widgets/currentSubscription';
import { Diagnocoins } from '@/widgets/diagnocoins';
import { FeatureFlags } from '@/widgets/featureFlags';
import {
  OrganizationApiKeys,
  organizationApiKeysMessages,
} from '@/widgets/organizationApiKeys';
import {
  OrganizationClients,
  organizationClientsTabMessages,
} from '@/widgets/organizationClients';
import {
  OrganizationStaff,
  organizationStaffMessages,
} from '@/widgets/organizationStaff';
import { Packages } from '@/widgets/packages';
import { SelectBillingZone } from '@/widgets/selectBillingZone';
import { StorageInfo } from '@/widgets/storageInfo';

import {
  markAsDSOModalMessages,
  unmarkAsDistributorModalMessages,
  unmarkAsDSOModalMessages,
} from './config';
import { useOrganizationStatusModal } from './hooks';
import styles from './Organization.module.scss';
import { MarkAsDistributorModal } from './ui/MarkAsDistributorModal';
import { OrganizationComment } from './ui/OrganizationComment/OrganizationComment';
import { OrganizationDistributor } from './ui/OrganizationDistributor/OrganizationDistributor';
import { OrganizationDSOSection } from './ui/OrganizationDSOSection/OrganizationDSOSection';
import { OrganizationInformationByDistributor } from './ui/OrganizationInformationByDistributor/OrganizationInformationByDistributor';

const { Title } = Typography;

export const Organization: FC = () => {
  const { organizationID } = useParams<{ organizationID: string }>();
  const { formatDate, formatMessage } = useIntl();

  const [searchParams, setSearchParams] = useSearchParams();

  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );
  const loading = useAppSelector(
    organizationModel.selectors.selectOrganizationLoading,
  );
  const owner = useAppSelector(
    organizationModel.selectors.selectOrganizationOwner,
  );
  const parentDSOOrganization = useAppSelector(
    organizationModel.selectors.selectParentDSOOrganization,
  );

  const isDistributorView = useAppSelector(
    authModel.selectors.selectIsDistributorView,
  );
  const listOrganization = useAppSelector(
    organizationsModel.selectors.selectById(organizationID),
  );
  const hasAdminBasicAccess = useAppSelector(
    authModel.selectors.selectHasAdminBasicAccess,
  );
  const canViewApiKeys = useAppSelector(
    authModel.selectors.selectCanViewApiKeys,
  );
  const canManageDistributors = useAppSelector(
    userModel.selectors.selectCanManageDistributors,
  );
  const canEditBillingZones = useAppSelector(
    authModel.selectors.selectCanEditBillingZones,
  );
  const canManageDSO = useAppSelector(userModel.selectors.selectCanManageDSO);

  const [isMarkAsDistributorModalOpen, setIsMarkAsDistributorModalOpen] =
    useState(false);
  const [isParentDSOEditorVisible, setIsParentDSOEditorVisible] =
    useState(false);

  // NOTE: The parent organization arrives in its own stream message, so it can
  // lag behind a just-changed parent — use it only while they match.
  const parentDSO =
    parentDSOOrganization &&
    parentDSOOrganization.ID === organization?.DSOParent?.ID
      ? parentDSOOrganization
      : undefined;

  const isDistributor = !!organization?.Distributor;
  const hasDistributor = !!organization?.OnboardedByDistributor?.ID;
  const isDSO = !!organization?.IsDSO;
  const billingProductBlockReason = getBillingProductBlockReason({
    canEditProducts: Boolean(
      organization?.YourPermissions?.BillingCanEditProducts,
    ),
  });

  // A distributor and the organizations it onboarded stay outside the DSO
  // hierarchy, so they can neither become a DSO nor get a parent DSO.
  const canManageDSOHierarchy =
    canManageDSO && !isDistributor && !hasDistributor;

  const organizationPermissions = organization?.YourPermissions as unknown as
    | Record<string, boolean>
    | undefined;
  const canViewFeatureFlags =
    organizationPermissions?.CanViewFeatureFlags ?? false;

  const tabParam = searchParams.get('tab');
  const activeTab =
    (tabParam === 'apiKeys' && canViewApiKeys) ||
    (tabParam === 'staff' && hasAdminBasicAccess) ||
    (tabParam === 'dsoClients' && isDSO) ||
    (tabParam === 'distributorClients' && isDistributor)
      ? tabParam
      : 'overview';

  const handleTabChange = (key: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', key);
    setSearchParams(nextParams, { replace: true });
  };

  const confirmMarkAsDSO = useOrganizationStatusModal({
    messages: markAsDSOModalMessages,
    thunk: userModel.thunks.markOrganizationAsDSO,
  });

  const confirmUnmarkAsDSO = useOrganizationStatusModal({
    messages: unmarkAsDSOModalMessages,
    thunk: userModel.thunks.unmarkOrganizationAsDSO,
    danger: true,
  });

  const confirmUnmarkAsDistributor = useOrganizationStatusModal({
    messages: unmarkAsDistributorModalMessages,
    thunk: userModel.thunks.unmarkOrganizationAsDistributor,
    danger: true,
  });

  const handleMarkAsDSO = () => confirmMarkAsDSO(organizationID);

  const handleUnmarkAsDSO = () => confirmUnmarkAsDSO(organizationID);

  const handleUnmarkDistributor = () =>
    confirmUnmarkAsDistributor(organizationID);

  return (
    <Layout.Content>
      <Breadcrumb
        style={{ padding: '24px 0' }}
        className="p2"
        items={[
          {
            title: (
              <Link className={styles.breadcrumb} to={generatePath(PATHS.main)}>
                {formatMessage({
                  id: 'breadcrumb.home',
                  defaultMessage: 'Home',
                })}
              </Link>
            ),
          },
          {
            title: `${organization?.Name ?? listOrganization?.Name ?? ''}`,
          },
        ]}
      />

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'overview',
            label: (
              <FormattedMessage
                id="organization.tabs.overview"
                defaultMessage="Overview"
              />
            ),
            children: (
              <Row gutter={16} style={{ rowGap: 24 }}>
                <Col md={24} lg={12}>
                  <Space
                    direction="vertical"
                    size="large"
                    style={{ display: 'flex' }}
                  >
                    {isDistributorView ? (
                      <OrganizationInformationByDistributor
                        organizationID={organizationID}
                      />
                    ) : (
                      <Card
                        style={{ height: '100%' }}
                        title={
                          <Title level={3} style={{ marginBottom: 0 }}>
                            <FormattedMessage
                              id="organization.title"
                              defaultMessage="Organization Information"
                            />
                          </Title>
                        }
                        bordered={false}
                        loading={loading !== 'succeeded'}
                        extra={
                          !hasDistributor && (
                            <AccountStripeSwitch
                              canEditAccountSettings={
                                organization?.YourPermissions
                                  ?.BillingCanEditAccountSettings ?? false
                              }
                            />
                          )
                        }
                      >
                        <div className={styles.cardBody}>
                          <div className={styles.info}>
                            <span className={styles.infoLabel}>
                              <FormattedMessage
                                id="organization.name"
                                defaultMessage="Name"
                              />
                            </span>
                            <span>{organization?.Name}</span>

                            <span className={styles.infoLabel}>
                              <FormattedMessage
                                id="organization.id"
                                defaultMessage="ID"
                              />
                            </span>
                            <span>{organization?.ID}</span>

                            <span className={styles.infoLabel}>
                              <FormattedMessage
                                id="global.created"
                                defaultMessage="Created"
                              />
                            </span>
                            <span>
                              {!!organization?.Created?.At?.seconds &&
                                formatDate(
                                  new Date(
                                    Number(organization?.Created?.At?.seconds) *
                                      1000,
                                  ),
                                  {
                                    year: 'numeric',
                                    month: 'long',
                                    day: '2-digit',
                                  },
                                )}
                            </span>

                            <span className={styles.infoLabel}>
                              <FormattedMessage
                                id="organization.owner"
                                defaultMessage="Owner"
                              />
                            </span>
                            <span>
                              {owner ? (
                                <TextLink
                                  to={generatePath(PATHS.user, {
                                    userID: owner.userID,
                                  })}
                                >
                                  {organization?.OwnerEmail}
                                </TextLink>
                              ) : (
                                organization?.OwnerEmail
                              )}

                              {!!owner?.fullName && (
                                <span className={styles.ownerName}>
                                  ({owner.fullName})
                                </span>
                              )}
                            </span>

                            <span className={styles.infoLabel}>
                              <FormattedMessage
                                id="organization.type"
                                defaultMessage="Type"
                              />
                            </span>
                            <span>
                              {organization?.Type !== undefined
                                ? formatMessage(
                                    i18n.organizationTypeDefaultMessages[
                                      organization.Type
                                    ],
                                  )
                                : '—'}
                            </span>

                            {organization && (
                              <OrganizationComment
                                organizationID={organizationID}
                                comment={organization?.Comment}
                                canManageComment={
                                  organization?.YourPermissions
                                    ?.CanManageComment ?? false
                                }
                              />
                            )}

                            {isDistributor && (
                              <>
                                <span className={styles.infoLabel}>
                                  <FormattedMessage
                                    id="organization.isDistributor"
                                    defaultMessage="Is Distributor"
                                  />
                                </span>
                                <span className={styles.tagCell}>
                                  <Tag color="blue">
                                    <FormattedMessage
                                      id="organizations.type.distributor"
                                      defaultMessage="Distributor"
                                    />
                                  </Tag>
                                  {organization?.Distributor?.Name && (
                                    <span>
                                      {' '}
                                      - {organization.Distributor.Name}
                                    </span>
                                  )}
                                  <Button
                                    type="default"
                                    danger
                                    size="small"
                                    disabled={!canManageDistributors}
                                    onClick={handleUnmarkDistributor}
                                  >
                                    <FormattedMessage
                                      id="organization.unmarkAsDistributor"
                                      defaultMessage="Remove Distributor Status"
                                    />
                                  </Button>
                                </span>
                              </>
                            )}

                            {organization && (
                              <OrganizationDistributor
                                canManage={canManageDistributors}
                                organization={organization}
                                onMarkAsDistributor={() =>
                                  setIsMarkAsDistributorModalOpen(true)
                                }
                              />
                            )}

                            {organization && (
                              <OrganizationDSOSection
                                canManageDSO={canManageDSO}
                                canManageHierarchy={canManageDSOHierarchy}
                                isDSO={isDSO}
                                isParentEditorVisible={isParentDSOEditorVisible}
                                organizationID={organizationID}
                                parentID={organization.DSOParent?.ID ?? ''}
                                parentEmail={organization.DSOParent?.Email}
                                parentName={parentDSO?.Name}
                                parentOrganization={parentDSO}
                                onMarkAsDSO={handleMarkAsDSO}
                                onParentEditorVisibleChange={
                                  setIsParentDSOEditorVisible
                                }
                                onUnmarkAsDSO={handleUnmarkAsDSO}
                              />
                            )}

                            <SelectBillingZone
                              canEditAccountSettings={
                                canEditBillingZones &&
                                (organization?.YourPermissions
                                  ?.BillingCanEditAccountSettings ??
                                  false)
                              }
                            />
                          </div>
                        </div>
                      </Card>
                    )}

                    <BillingInfo
                      canEditAccountContacts={
                        organization?.YourPermissions
                          ?.BillingCanEditAccountContacts ?? false
                      }
                    />

                    <BillingHistory blockReason={billingProductBlockReason} />
                  </Space>
                </Col>

                <Col md={24} lg={12}>
                  <Space
                    direction="vertical"
                    size="large"
                    style={{ display: 'flex' }}
                  >
                    <CurrentSubscription organizationID={organizationID} />

                    <Packages organizationID={organizationID} />

                    <Diagnocoins organizationID={organizationID} />

                    {canViewFeatureFlags && <FeatureFlags />}

                    {hasAdminBasicAccess && <StorageInfo />}
                  </Space>
                </Col>
              </Row>
            ),
          },
          ...(canViewApiKeys
            ? [
                {
                  key: 'apiKeys',
                  label: formatMessage(organizationApiKeysMessages.tab),
                  children: (
                    <OrganizationApiKeys
                      organizationID={organizationID}
                      canCreate={
                        organization?.YourPermissions?.APIKeyCanCreate ?? false
                      }
                      canDelete={
                        organization?.YourPermissions?.APIKeyCanDelete ?? false
                      }
                    />
                  ),
                },
              ]
            : []),
          ...(hasAdminBasicAccess
            ? [
                {
                  key: 'staff',
                  label: formatMessage(organizationStaffMessages.tab),
                  children: <OrganizationStaff />,
                },
              ]
            : []),
          ...(isDSO && organization?.OwnerEmail
            ? [
                {
                  key: 'dsoClients',
                  label: formatMessage(organizationClientsTabMessages.dso),
                  children:
                    activeTab === 'dsoClients' ? (
                      <OrganizationClients
                        kind="dso"
                        ownerEmail={organization.OwnerEmail}
                      />
                    ) : null,
                },
              ]
            : []),
          ...(isDistributor && organization?.OwnerEmail
            ? [
                {
                  key: 'distributorClients',
                  label: formatMessage(
                    organizationClientsTabMessages.distributor,
                  ),
                  children:
                    activeTab === 'distributorClients' ? (
                      <OrganizationClients
                        kind="distributor"
                        ownerEmail={organization.OwnerEmail}
                      />
                    ) : null,
                },
              ]
            : []),
        ]}
      />

      {organization && (
        <MarkAsDistributorModal
          isOpen={isMarkAsDistributorModalOpen}
          onCloseHandler={() => setIsMarkAsDistributorModalOpen(false)}
          organization={organization}
        />
      )}
    </Layout.Content>
  );
};
